import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Modal, useNotify } from '@hotjar/hotsauce';

import { useIntegrations } from '../../hooks/use-integrations';
import { disconnect } from '../../domains/ms-teams/api';

export interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  siteId: string | null;
  siteName: string | null;
}

export const DisableMsTeamsModal = ({
  isOpen,
  onRequestClose,
  onSuccess,
  onError,
  siteId,
  siteName,
}: Props): JSX.Element => {
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const { t } = useTranslation();
  const { refreshTokens } = useIntegrations(Number(siteId));
  const { showToast } = useNotify();

  const handleDisconnect = () => {
    if (siteId) {
      setIsDisconnecting(true);
      disconnect(siteId)
        .then(() => {
          showToast({
            text: t('integrations:ms_teams.disable-modal.notify-success', { siteName }),
            kind: 'success',
          });
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch((error) => {
          if (error.message) {
            showToast({ text: error.message, kind: 'danger' });
          }
          if (onError) {
            onError();
          }
        })
        .finally(() => {
          setIsDisconnecting(false);
          refreshTokens();
        });
    }
  };

  return (
    <Modal
      contentLabel={t('integrations:ms_teams.disable-modal.content-label')}
      isOpen={isOpen}
      onClose={onRequestClose}
    >
      <Modal.Header title={t('integrations:ms_teams.disable-modal.title')} />
      <Modal.Body>
        <p>{t('integrations:ms_teams.disable-modal.description', { siteName })}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={onRequestClose} isDisabled={isDisconnecting}>
          {t('integrations:ms_teams.disable-modal.cancel')}
        </Button>
        <Button
          kind="danger"
          onClick={handleDisconnect}
          type="submit"
          data-testid="confirm"
          isDisabled={isDisconnecting}
        >
          {t('integrations:ms_teams.disable-modal.action')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
